import React, { useState } from "react";
import { supabase } from "../supabase/supabase";
import { Users, Copy, Check, Trash2, Share2 } from "lucide-react";
import { useLeague } from "../context/LeagueContext";

function LeagueMembers() {
  const { leagueData, members, isAdmin, isDraftComplete } = useLeague();
  const [copied, setCopied] = useState(false);

  const leagueId = leagueData?.id;
  const inviteUrl = `${window.location.origin}/join/${leagueData?.invite_code}`;
  const spotsLeft = (leagueData?.max_managers || 0) - members.length;

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${leagueData?.name} on Lineup`,
          text: `Use invite code ${leagueData?.invite_code} to join my Lineup league.`,
          url: inviteUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the league?`)) return;
    await supabase
      .from("league_members")
      .delete()
      .eq("league_id", leagueId)
      .eq("user_id", userId);
  };

  return (
    <div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 pb-24">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "var(--color-band-bg)" }}>
            <Users size={24} style={{ color: "var(--color-brand)" }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: "var(--color-text-strong)" }}>
            {leagueData?.name}
          </h1>
          <p style={{ color: "var(--color-text-muted)" }}>
            {members.length} / {leagueData?.max_managers} managers
            {spotsLeft > 0 && ` · ${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} remaining`}
          </p>
        </div>

        {/* Invite Card, admin only, pre-draft only */}
        {isAdmin && !isDraftComplete && (
          <div className="bg-surface-card rounded-2xl p-6 border border-hairline shadow-sm mb-6">
            <h2 className="text-base font-bold mb-1" style={{ color: "var(--color-text-strong)" }}>Invite players</h2>
            <p className="text-sm mb-4" style={{ color: "var(--color-text-muted)" }}>Share this link or code with your managers</p>

            {/* Invite code */}
            <div className="text-center rounded-xl px-4 py-4 mb-3" style={{ backgroundColor: "var(--color-band-bg)", border: "1px solid var(--color-band-border)" }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "var(--color-text-muted)" }}>Invite code</p>
              <p className="font-bold text-3xl tracking-[0.3em]" style={{ color: "var(--color-brand)" }}>
                {leagueData?.invite_code || '—'}
              </p>
            </div>

            {/* Full URL */}
            <div className="bg-surface-subtle border border-hairline rounded-xl px-4 py-3 text-ink-muted text-sm mb-3 break-all">
              {inviteUrl}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-surface-card border border-hairline hover:bg-surface-subtle text-ink-muted rounded-xl font-semibold text-sm transition-colors"
              >
                {copied ? <Check size={16} className="text-success-text" /> : <Copy size={16} />}
                {copied ? "Copied" : "Copy Link"}
              </button>
              <button
                onClick={handleShare}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand hover:bg-brand text-white rounded-xl font-semibold text-sm transition-colors"
              >
                <Share2 size={16} />
                Share
              </button>
            </div>
          </div>
        )}

        {/* Members list */}
        <div className="bg-surface-card rounded-2xl p-6 border border-hairline shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <Users size={18} className="text-brand" />
            <h2 className="text-base font-bold text-ink">Managers</h2>
          </div>

          {members.length === 0 ? (
            <p className="text-ink-faint text-center py-4">No managers have joined yet.</p>
          ) : (
            <div className="space-y-3">
              {members.map((m, i) => (
                <div key={m.user_id} className="flex items-center gap-4 bg-surface-subtle rounded-xl p-4 border border-hairline">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden">
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-brand flex items-center justify-center font-bold text-white text-sm">
                        {m.first_name?.[0]?.toUpperCase() || (i + 1)}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink truncate">
                      {m.first_name} {m.last_name}
                    </div>
                    <div className="text-sm text-ink-muted truncate">{m.team_name}</div>
                  </div>

                  {isDraftComplete && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-ink font-bold">{m.points ?? 0}</div>
                      <div className="text-xs text-ink-faint">pts</div>
                    </div>
                  )}

                  {isAdmin && !isDraftComplete && (
                    <button
                      onClick={() => handleRemoveMember(m.user_id, `${m.first_name} ${m.last_name}`.trim())}
                      className="ml-2 p-2 bg-danger-bg border border-danger-border text-danger-text rounded-lg hover:bg-danger-bg transition-colors flex-shrink-0"
                      title="Remove manager"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Empty spots */}
          {!isDraftComplete && spotsLeft > 0 && (
            <div className="mt-3 space-y-2">
              {Array.from({ length: spotsLeft }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 bg-surface-subtle rounded-xl p-4 border border-dashed border-hairline">
                  <div className="w-10 h-10 bg-surface-subtle rounded-full flex items-center justify-center text-ink-faint text-sm flex-shrink-0">
                    {members.length + i + 1}
                  </div>
                  <div className="text-ink-faint text-sm">Waiting for manager...</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeagueMembers;
